import {
  Show,
  createResource,
  createSignal,
  createUniqueId,
  onCleanup,
  onMount,
  type Component,
} from "solid-js";
import { getProjectQuota } from "../../api/projects";
import { getTenantQuota } from "../../api/tenants";
import type {
  ProjectId,
  ProjectQuotaStatus,
  QuotaScope,
  TenantId,
  TenantQuotaStatus,
} from "../../api/types";
import {
  useOptionalProjectId,
  useProjectTenantId,
} from "../../contexts/ProjectContext";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";

interface Props {
  /** Re-run the AI output on the standard model. */
  onRegenerate: () => void;
  regenerating?: boolean;
  /**
   * Which ceiling bound this output to the fast tier (FLOW-930). `"project"`
   * names the project in the copy and reads the project's budget for the CTA;
   * `"tenant"` and `undefined` keep the org-wide wording.
   */
  scope?: QuotaScope;
}

/**
 * Marks an AI output the quota fallback tier produced (FLOW-794), and explains
 * what to do about it: top up when the budget that bound is at its hard stop,
 * regenerate otherwise. The quota is only read once the popover opens, and it
 * is read from the budget named by `scope` — reading the org's quota at a
 * project ceiling reports `normal` and offers a regenerate that degrades again
 * (FLOW-930 D5).
 */
export const DegradedOutputBadge: Component<Props> = (props) => {
  const tenantId = useProjectTenantId();
  const projectId = useOptionalProjectId();
  const [open, setOpen] = createSignal(false);
  const [quota] = createResource<
    TenantQuotaStatus | ProjectQuotaStatus | undefined,
    { scope?: QuotaScope; tenantId?: TenantId; projectId?: ProjectId }
  >(
    () =>
      open()
        ? { scope: props.scope, tenantId: tenantId(), projectId: projectId() }
        : undefined,
    (s) =>
      s.scope === "project"
        ? s.projectId
          ? getProjectQuota(s.projectId)
          : undefined
        : s.tenantId
          ? getTenantQuota(s.tenantId)
          : undefined,
  );
  const popoverId = createUniqueId();
  const headingId = createUniqueId();
  let root: HTMLDivElement | undefined;
  let pill: HTMLButtonElement | undefined;

  // Reading `quota()` after a rejection rethrows, so every read goes through
  // this. `undefined` means the quota is *unknown*: the popover has never been
  // opened (falsy source, so Solid leaves the resource `unresolved` — `loading`
  // is false and `quota()` is undefined), the id the named scope needs is
  // missing so the fetcher asked for nothing, or the read failed. An unknown
  // quota must never be mistaken for a healthy one.
  const quotaValue = () => (quota.error ? undefined : quota());
  // Offering "Regenerate" needs positive evidence that the budget which bound
  // this output — the project's at `scope === "project"`, the org's otherwise —
  // is below its stop.
  const canRegenerate = () => {
    const q = quotaValue();
    return q !== undefined && q.level !== "hard_stop";
  };
  const upgradeUrl = () => quotaValue()?.upgrade_url;

  // Escape only ever needs to close the popover while focus is inside this
  // widget: the popover opens from a click on `pill`, and every focusable
  // element it renders (the pill, the upgrade link, the regenerate button)
  // lives inside `root`. So a root-level handler sees every Escape this
  // widget cares about, and there's no separate `document` path to keep in
  // sync with it — the bubble is stopped here before it can reach ancestors
  // like QuickCreateCard's or BacklogPage's own Escape handling.
  const onKeyDown = (e: KeyboardEvent) => {
    if (open() && e.key === "Escape") {
      e.stopPropagation();
      setOpen(false);
    }
  };
  const onPointerDown = (e: PointerEvent) => {
    if (open() && root && !root.contains(e.target as Node)) setOpen(false);
  };
  onMount(() => {
    document.addEventListener("pointerdown", onPointerDown);
  });
  onCleanup(() => {
    document.removeEventListener("pointerdown", onPointerDown);
  });

  return (
    <div ref={root} class="relative inline-block" onKeyDown={onKeyDown}>
      <button
        ref={pill}
        type="button"
        data-testid="degraded-output-badge"
        aria-haspopup="dialog"
        aria-expanded={open()}
        aria-controls={popoverId}
        class="inline-flex items-center gap-1 h-6 px-1.5 text-xs font-medium rounded-sm border bg-signal-amber-tint text-signal-amber border-signal-amber/25 focus-ring"
        onClick={() => setOpen(!open())}
      >
        <span
          aria-hidden="true"
          class="w-1.5 h-1.5 rounded-full bg-current shrink-0"
        />
        Fast model
      </button>
      <Show when={open()}>
        <div
          id={popoverId}
          role="dialog"
          aria-labelledby={headingId}
          data-testid="degraded-output-popover"
          class="absolute z-20 top-full left-0 mt-1 w-72 rounded-md border border-line bg-surface p-3 shadow-overlay text-sm text-ink-2 space-y-2"
        >
          <h3 id={headingId} class="font-semibold text-ink">
            Generated on the fast model
          </h3>
          <Show
            when={props.scope === "project"}
            fallback={
              <p>
                Your organization has used its AI token budget for this period,
                so this was produced by the lighter fast model. Depth and detail
                may differ from the standard model.
              </p>
            }
          >
            <p>
              This project has used its AI token budget for this month, so this
              was produced by the lighter fast model. Depth and detail may
              differ from the standard model.
            </p>
          </Show>
          <Show when={!quota.loading} fallback={<Spinner size="sm" />}>
            <Show
              when={canRegenerate()}
              fallback={
                <Show
                  when={upgradeUrl()}
                  fallback={
                    <Show
                      when={props.scope === "project"}
                      fallback={
                        <p>Ask an organization admin to raise the AI budget.</p>
                      }
                    >
                      <p>
                        Ask an organization admin to raise this project's AI
                        budget.
                      </p>
                    </Show>
                  }
                >
                  {(url) => (
                    <a
                      data-testid="degraded-upgrade-link"
                      href={url()}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-signal-blue underline"
                    >
                      Add AI tokens
                    </a>
                  )}
                </Show>
              }
            >
              {/* `Button` has no rest spread, so the hook goes on a wrapper —
                  click the inner <button>, not this span. */}
              <span data-testid="degraded-regenerate">
                <Button
                  size="sm"
                  variant="secondary"
                  loading={props.regenerating}
                  onClick={() => {
                    // The popover unmounts under the pointer; move focus back
                    // to the pill before it takes the button with it.
                    pill?.focus();
                    setOpen(false);
                    props.onRegenerate();
                  }}
                >
                  Regenerate with advanced model
                </Button>
              </span>
            </Show>
          </Show>
        </div>
      </Show>
    </div>
  );
};
